/**
 * AADHYAA — Route Configuration
 * =============================
 * The complete `<Routes>` tree extracted from App.js.
 *
 * Why this file?
 *   App.js used to be 877 lines, dominated by a ~620-line route declaration
 *   block. Lifting that block here lets App.js focus on the provider tree
 *   and global shell (Toaster, OfflineIndicator, etc.) — i.e. its actual
 *   responsibility. Adding a new route now means editing this file, not the
 *   entry point.
 *
 * The component accepts `user` as a prop (read from AuthContext at the call
 * site) and renders the matching <Route> entries. ProtectedRoute (auth +
 * onboarding gate) is applied per route as before.
 */
import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";

// Critical-path (eagerly imported in App.js, re-imported here)
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";

// All page modules (lazy)
import {
  InsightsPage, InsightsPillarPage, InsightsArticlePage, PricingPage,
  LegalPage, TermsPage, AboutPage, PublicNakshatraPage, PublicRashiPage,
  PublicLearnListPages, PublicNakshatraListPage, PublicKundliPage,
  PublicCompatibilityPage, AdminAnalyticsPage, SLADashboardPage,
  PrivacyPolicyPage, HomePage, MyLifePage, TimingPage, RelationshipsPage,
  LearnGrowPage, ReportsPage, AskPage, OnboardingPage, ProfileCompletePage, ReviewerLoginPage, JourneyDashboard,
  MyStarsPage, FamilyPage, LearnPage, ToolsPage, PanchangPage, MuhurtaPage,
  MuhurtaComparePage, ChecklistPage, MoodJournalPage, CircadianPage,
  SettingsPage, EkadashiPage, KundliPage, RemediesPage, CompatibilityPage,
  HoroscopePage, RitualReminderPage, LifePathPage, GuidedRitualsPage,
  KidsCoursesPage, MantrasPage, FamilyDashboardPage, AstroReportPage,
  HealthConnectionPage, SubscriptionSuccessPage, SubscriptionCancelPage,
  SharePage, VerifiedReadingPage, MyLifeReadingsPage,
  MarriageCompatibilityPage, PurchaseHistoryPage, MyAccountPage,
  AccuracyDashboard, AstroIntelligencePage, AdminDashboardPage,
  ConsultationPage, PrashnaVarshphalPage, ReportViewPage, MyPurchasesPage,
  MethodologyPage, GlossaryPage, GettingStartedPage, BetaAdminDashboard,
  EnterpriseAdminDashboard, ResetPasswordPage, AnalyticsDashboard,
  ConversionDashboard, DashaExplorerPage, VedicDepthHub, HabitsPage,
  KnowledgeHubPage, NotificationsPage, TrustDashboardPage,
  ReportsCatalogPage, FamilyIntelligencePage, AIIntelligencePage,
  PrivacyDashboardPage,
} from "./lazy-pages";

export const AppRoutesTree = ({ user }) => (
  <Routes>
    {/* STAGE 0-1: Discovery & First Launch */}
    <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />

    {/* STAGE 2: Account Creation (Email-based — iter 283 reverted from Mobile OTP)
        Guard rules:
          - not signed in           → show AuthPage (email sign in / sign up)
          - signed in, no chart yet → /onboarding
          - signed in, chart ready  → /dashboard
        Email signup form captures `name` inline, so the legacy /profile/complete
        page is only reached by lingering OTP-only users (still routed below). */}
    <Route
      path="/auth"
      element={
        !user
          ? <AuthPage />
          : (!user.email && !user.profile_complete)
            ? <Navigate to="/profile/complete" replace />
            : !user.has_birth_chart
              ? <Navigate to="/onboarding" replace />
              : <Navigate to="/dashboard" replace />
      }
    />
    {/* P1: /login alias so direct links / SEO entry points don't hit a blank route */}
    <Route path="/login" element={<Navigate to="/auth" replace />} />
    <Route path="/signup" element={<Navigate to="/auth?mode=register" replace />} />
    {/* /auth/reviewer kept as alias for Razorpay/reviewer docs that already cite the URL */}
    <Route path="/auth/reviewer" element={<Navigate to="/auth" replace />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />

    {/* PUBLIC: Insights — Authority-Building Knowledge Hub */}
    <Route path="/insights" element={<InsightsPage />} />
    <Route path="/insights/:pillarSlug" element={<InsightsPillarPage />} />
    <Route path="/insights/:pillarSlug/:articleSlug" element={<InsightsArticlePage />} />

    {/* PUBLIC: 27 Nakshatras + 12 Rashis SEO Landing Pages (Phase 16 Move 3c) */}
    <Route path="/learn/rashi" element={<PublicLearnListPages />} />
    <Route path="/learn/rashi/:slug" element={<PublicRashiPage />} />
    <Route path="/learn/nakshatra" element={<PublicNakshatraListPage />} />
    <Route path="/learn/nakshatra/:slug" element={<PublicNakshatraPage />} />
    {/* PUBLIC: Free Kundli Generator (Phase 16 Move 3b) */}
    <Route path="/learn/kundli" element={<PublicKundliPage />} />
    {/* PUBLIC: Free Kundli Matching / Compatibility (Phase 16 Move 3d) */}
    <Route path="/learn/compatibility" element={<PublicCompatibilityPage />} />
    {/* ADMIN: Content Performance Analytics (Phase 16) */}
    <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalyticsPage /></ProtectedRoute>} />
    <Route path="/admin/sla" element={<ProtectedRoute><SLADashboardPage /></ProtectedRoute>} />
    <Route path="/privacy" element={<PrivacyPolicyPage />} />

    {/* PUBLIC: About & Press Page */}
    <Route path="/about" element={<AboutPage />} />

    {/* PUBLIC: Legacy Wisdom Hub (redirect to new Insights) */}
    <Route path="/wisdom" element={<Navigate to="/insights" replace />} />

    {/* /auth/reviewer alias now lives above (line ~78) — redirects to /auth.
        We keep the ReviewerLoginPage component file untouched so the legacy
        page can be revived in one line if ever needed. */}

    {/* STAGE 3: Core Onboarding — Does NOT require onboarding check (to avoid loop) */}
    <Route
      path="/profile/complete"
      element={
        <ProtectedRoute requireOnboarding={false}>
          <ProfileCompletePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/onboarding"
      element={
        <ProtectedRoute requireOnboarding={false}>
          <OnboardingPage />
        </ProtectedRoute>
      }
    />

    {/* ========== POST-LOGIN: 7-SECTION ARCHITECTURE (PRIMARY) ========== */}
    <Route path="/dashboard" element={<ProtectedRoute><Navigate to="/home" replace /></ProtectedRoute>} />
    <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
    <Route path="/my-life" element={<ProtectedRoute><MyLifePage /></ProtectedRoute>} />
    <Route path="/timing" element={<ProtectedRoute><TimingPage /></ProtectedRoute>} />
    <Route path="/relationships" element={<ProtectedRoute><RelationshipsPage /></ProtectedRoute>} />
    {/* Section 5: ASK AADHYAA — uses existing AskPage (declared below) */}
    <Route path="/learn-grow" element={<ProtectedRoute><LearnGrowPage /></ProtectedRoute>} />
    <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
    {/* ========== END 7-SECTION ARCHITECTURE ========== */}

    {/* Legacy routes redirect to 7-section architecture */}
    <Route path="/today" element={<ProtectedRoute><JourneyDashboard /></ProtectedRoute>} />
    <Route path="/my-stars" element={<ProtectedRoute><MyStarsPage /></ProtectedRoute>} />
    <Route path="/ask" element={<ProtectedRoute><AskPage /></ProtectedRoute>} />
    <Route path="/family" element={<ProtectedRoute><FamilyPage /></ProtectedRoute>} />
    <Route path="/learn" element={<ProtectedRoute><LearnPage /></ProtectedRoute>} />
    <Route path="/tools" element={<ProtectedRoute><ToolsPage /></ProtectedRoute>} />

    {/* LEGACY/DEEP ROUTES — Accessible from Tools */}
    <Route path="/panchang" element={<ProtectedRoute><PanchangPage /></ProtectedRoute>} />
    <Route path="/muhurta" element={<ProtectedRoute><MuhurtaPage /></ProtectedRoute>} />
    <Route path="/muhurta/compare" element={<ProtectedRoute><MuhurtaComparePage /></ProtectedRoute>} />
    <Route path="/checklist" element={<ProtectedRoute><ChecklistPage /></ProtectedRoute>} />
    <Route path="/mood" element={<ProtectedRoute><MoodJournalPage /></ProtectedRoute>} />
    <Route path="/circadian" element={<ProtectedRoute><CircadianPage /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
    <Route path="/ekadashi" element={<ProtectedRoute><EkadashiPage /></ProtectedRoute>} />
    <Route path="/kundli" element={<ProtectedRoute><KundliPage /></ProtectedRoute>} />
    <Route path="/astro-report" element={<ProtectedRoute><AstroReportPage /></ProtectedRoute>} />

    {/* ============================================================= */}
    {/* REPORTS — FINALIZED 12-REPORT SYSTEM                          */}
    {/* ============================================================= */}
    <Route path="/report/:slug" element={<ProtectedRoute><ReportViewPage /></ProtectedRoute>} />
    <Route path="/my-purchases" element={<ProtectedRoute><MyPurchasesPage /></ProtectedRoute>} />

    {/* Legacy Report Redirects — Redirect to new Reports hub */}
    <Route path="/report/snapshot" element={<Navigate to="/reports" replace />} />
    <Route path="/report/natal" element={<Navigate to="/reports" replace />} />
    <Route path="/report/cosmic-profile" element={<Navigate to="/reports" replace />} />
    <Route path="/report/year-timing" element={<Navigate to="/reports" replace />} />
    <Route path="/report/relationship" element={<Navigate to="/reports" replace />} />
    <Route path="/report/career" element={<Navigate to="/reports" replace />} />
    <Route path="/report/career-insights" element={<Navigate to="/reports" replace />} />
    <Route path="/report/health" element={<Navigate to="/reports" replace />} />
    <Route path="/report/wellbeing" element={<Navigate to="/reports" replace />} />
    <Route path="/report/remedies" element={<Navigate to="/reports" replace />} />
    <Route path="/report-repository" element={<Navigate to="/reports" replace />} />

    {/* Tools & consultations */}
    <Route path="/health-connect" element={<ProtectedRoute><HealthConnectionPage /></ProtectedRoute>} />
    <Route path="/remedies" element={<ProtectedRoute><RemediesPage /></ProtectedRoute>} />
    <Route path="/consultation" element={<ProtectedRoute><ConsultationPage /></ProtectedRoute>} />
    <Route path="/compatibility" element={<ProtectedRoute><CompatibilityPage /></ProtectedRoute>} />
    <Route path="/horoscope" element={<ProtectedRoute><HoroscopePage /></ProtectedRoute>} />
    <Route path="/ritual" element={<ProtectedRoute><RitualReminderPage /></ProtectedRoute>} />
    <Route path="/lifepath" element={<ProtectedRoute><LifePathPage /></ProtectedRoute>} />
    <Route path="/guided-rituals" element={<ProtectedRoute><GuidedRitualsPage /></ProtectedRoute>} />
    <Route path="/kids-courses" element={<ProtectedRoute><KidsCoursesPage /></ProtectedRoute>} />
    <Route path="/mantras" element={<ProtectedRoute><MantrasPage /></ProtectedRoute>} />
    <Route path="/family-dashboard" element={<ProtectedRoute><FamilyDashboardPage /></ProtectedRoute>} />

    {/* Commerce */}
    <Route path="/pricing" element={<PricingPage />} />
    <Route path="/purchase-history" element={<ProtectedRoute><PurchaseHistoryPage /></ProtectedRoute>} />
    <Route path="/account" element={<ProtectedRoute><MyAccountPage /></ProtectedRoute>} />
    <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
    <Route path="/subscription/cancel" element={<ProtectedRoute><SubscriptionCancelPage /></ProtectedRoute>} />

    {/* Sharing & deep-link surfaces */}
    <Route path="/share" element={<ProtectedRoute><SharePage /></ProtectedRoute>} />
    <Route path="/verified-reading" element={<ProtectedRoute><VerifiedReadingPage /></ProtectedRoute>} />
    <Route path="/life-readings" element={<ProtectedRoute><MyLifeReadingsPage /></ProtectedRoute>} />
    <Route path="/marriage-compatibility" element={<ProtectedRoute><MarriageCompatibilityPage /></ProtectedRoute>} />
    <Route path="/accuracy" element={<ProtectedRoute><AccuracyDashboard /></ProtectedRoute>} />
    <Route path="/astro-intelligence" element={<ProtectedRoute><AstroIntelligencePage /></ProtectedRoute>} />

    {/* Legal */}
    <Route path="/legal" element={<LegalPage />} />
    <Route path="/ethics" element={<Navigate to="/legal#ethics" replace />} />
    {/* /privacy declared above; this /privacy in old App.js was overridden by the PrivacyPolicyPage route. Restore the redirect chain only if the policy page is decommissioned. */}
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/disclaimers" element={<Navigate to="/legal#disclaimers" replace />} />
    <Route path="/ai-policy" element={<Navigate to="/legal#ai" replace />} />
    <Route path="/data-policy" element={<Navigate to="/legal#data" replace />} />
    <Route path="/consent-policy" element={<Navigate to="/legal#consent" replace />} />
    <Route path="/health-policy" element={<Navigate to="/legal#health" replace />} />

    {/* Admin surfaces */}
    <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
    <Route path="/prashna-varshphal" element={<ProtectedRoute><PrashnaVarshphalPage /></ProtectedRoute>} />
    <Route path="/beta-admin" element={<ProtectedRoute><BetaAdminDashboard /></ProtectedRoute>} />
    {/* Enterprise Admin Dashboard — Independent Auth (12 Sections) */}
    <Route path="/enterprise-admin" element={<EnterpriseAdminDashboard />} />
    <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
    <Route path="/conversion-dashboard" element={<ProtectedRoute><ConversionDashboard /></ProtectedRoute>} />

    {/* Public reference content */}
    <Route path="/methodology" element={<MethodologyPage />} />
    <Route path="/glossary" element={<GlossaryPage />} />
    {/* Getting Started / Vedic 101 — Public Access (Western User Accessibility) */}
    <Route path="/getting-started" element={<GettingStartedPage />} />
    <Route path="/vedic-101" element={<GettingStartedPage />} />

    {/* PHASE 1-2: New Core Surfaces */}
    <Route path="/dasha-explorer" element={<ProtectedRoute><DashaExplorerPage /></ProtectedRoute>} />
    <Route path="/vedic-depth" element={<ProtectedRoute><VedicDepthHub /></ProtectedRoute>} />
    <Route path="/habits" element={<ProtectedRoute><HabitsPage /></ProtectedRoute>} />
    <Route path="/knowledge" element={<ProtectedRoute><KnowledgeHubPage /></ProtectedRoute>} />

    {/* PHASE 5: Notifications, Trust, Reports Catalog, Family Intelligence */}
    <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
    <Route path="/trust-dashboard" element={<ProtectedRoute><TrustDashboardPage /></ProtectedRoute>} />
    <Route path="/reports-catalog" element={<ProtectedRoute><ReportsCatalogPage /></ProtectedRoute>} />
    <Route path="/family-intelligence" element={<ProtectedRoute><FamilyIntelligencePage /></ProtectedRoute>} />
    <Route path="/ai-intelligence" element={<ProtectedRoute><AIIntelligencePage /></ProtectedRoute>} />
    <Route path="/privacy-dashboard" element={<ProtectedRoute><PrivacyDashboardPage /></ProtectedRoute>} />
  </Routes>
);

export default AppRoutesTree;
